/**
 * Latin digits for everything catalogued — years, ids, centimetres, currency.
 * DESIGN.md sets those in IBM Plex Mono, which has no Persian numerals, and
 * mixing digit systems inside one inventory is worse than either choice alone.
 * This holds in both languages: the record reads the same either way.
 *
 * A record's *date* is the exception. It is prose, not an inventory value, so
 * it follows the reader's calendar and that calendar's own numerals.
 */
import { getLang, type Lang } from "./lang";

const decimal = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const money = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "";
  return decimal.format(Number(value));
}

export function formatMoney(value: number | null | undefined, code?: string): string {
  if (value === null || value === undefined) return "";
  return code ? `${money.format(value)} ${code}` : money.format(value);
}

const grams = new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 });
const whole = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

/** Scale readings keep their milligrams: 4.25 g, 12.105 g. */
export function formatGrams(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "";
  return grams.format(Number(value));
}

/** The board quotes rial; the trade speaks toman. One toman is ten rial. */
export function rialToToman(rial: number): number {
  return rial / 10;
}

export function formatToman(rial: number | null | undefined): string {
  if (rial === null || rial === undefined || !Number.isFinite(rial)) return "";
  return whole.format(Math.round(rialToToman(rial)));
}

const times: Record<Lang, Intl.DateTimeFormat> = {
  fa: new Intl.DateTimeFormat("fa-IR", {
    hour: "2-digit",
    minute: "2-digit",
    month: "long",
    day: "numeric",
  }),
  en: new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  }),
};

/** A market "as of": the day and the minute, in the reader's calendar. */
export function formatTime(iso: string | null | undefined, lang: Lang = getLang()): string {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : times[lang].format(date);
}

const MB = 1024 * 1024;
const KB = 1024;

export function formatBytes(bytes: number): string {
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  if (bytes >= KB) return `${Math.round(bytes / KB)} KB`;
  return `${bytes} B`;
}

/** Jalali in Persian, Gregorian in English — the calendar follows the reader. */
const dateFormats: Record<Lang, Intl.DateTimeFormat> = {
  fa: new Intl.DateTimeFormat("fa-IR", { year: "numeric", month: "long", day: "numeric" }),
  en: new Intl.DateTimeFormat("en-GB", { year: "numeric", month: "long", day: "numeric" }),
};

export function formatDate(iso: string, lang: Lang = getLang()): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : dateFormats[lang].format(date);
}

/**
 * Chooses the display font for a title. Fraunces italic is a Latin face; a
 * Persian title set in it would fall back mid-string and look broken.
 */
export function isLatinScript(text: string): boolean {
  const letters = text.replace(/[^\p{L}]/gu, "");
  if (!letters) return false;
  const latin = letters.match(/\p{Script=Latin}/gu)?.length ?? 0;
  return latin / letters.length > 0.6;
}
